/**
 * Is a property record flagged as a development? ONE rule, for the pages and (when the add
 * function comes to hold such homes back itself) for the functions (EST-192, Oct 2026).
 *
 * Lee, 9 Oct 2026: "we don't want new homes (we are targetting home sellers only) and
 * definitely not land."
 *
 * The scrapers write two flags on a record: `development`, from Rightmove's search card, and
 * `agent.isDeveloper`. Counted on 9 Oct 2026 (56,700 records): `development` was true on 493;
 * `agent.isDeveloper` was true on NONE; 13,460 records, saved before the scrapers wrote
 * either, had neither field. Letters had gone to 132 flagged homes.
 *
 * THREE ANSWERS, NOT TWO:
 *  - FLAGGED      either flag is a real `true`. Nothing else is: not the text "true", not 1.
 *  - NOT_FLAGGED  neither is true, at least one field is there, and EVERY field that is there
 *                 is a real `false`: a record that could have said, and said no.
 *  - NO_FLAG      it cannot be told: neither field is there (a record saved before the
 *                 scrapers wrote the flags, or brought in by an import), OR a field that is
 *                 there holds something that is neither true nor false. ⚠️ This is "cannot
 *                 tell", and is never to be shown or counted as "not flagged".
 *
 * ⚠️ AN UNREADABLE VALUE IS NOT OUTVOTED BY A REAL `false` ON THE OTHER FLAG (the second
 * review of PR #114, 9 Oct 2026). The agent's flag has never said yes on live, so the
 * listing's flag is the only one that flags. If a scraper or a script ever wrote that one as
 * the text "true" or the number 1, a rule that let the agent's `false` answer for the record
 * would call every such home "not flagged", and it would be added with no word that it could
 * not be told. That is the do-not-contact lesson of the same day (a mark stored as text).
 * A field that is null, or not there, is not a value: it does not count either way.
 *
 * ⚠️ SAY "flagged as a development", NOT "a new home". What Rightmove's flag marks has been
 * counted, not looked at.
 *
 * Pure: a record in, a word out. Tests: src/models/__tests__/developmentFlag.test.js.
 */
export const FLAGGED = 'flagged';
export const NOT_FLAGGED = 'not-flagged';
export const NO_FLAG = 'no-flag';

const agentOf = (record) => (record && record.agent !== null && typeof record.agent === 'object' ? record.agent : null);

/** FLAGGED, NOT_FLAGGED or NO_FLAG. */
export function developmentFlagOf(record) {
  const r = record !== null && typeof record === 'object' ? record : {};
  const agent = agentOf(r);
  const listing = r.development;
  const agents = agent ? agent.isDeveloper : undefined;
  if (listing === true || agents === true) return FLAGGED;
  // what each field that is THERE says (null, like nothing at all, is not a value)
  const said = [listing, agents].filter((v) => v !== undefined && v !== null);
  if (said.length > 0 && said.every((v) => v === false)) return NOT_FLAGGED;
  return NO_FLAG;
}

/** True only on a real `true` in either flag. */
export const flaggedAsDevelopment = (record) => developmentFlagOf(record) === FLAGGED;

/** Which flag says so, for a person: "the listing", "the agent", or "the listing and the agent". Null when not flagged. */
export function flaggedBy(record) {
  const r = record !== null && typeof record === 'object' ? record : {};
  const agent = agentOf(r);
  const listing = r.development === true; const agents = Boolean(agent) && agent.isDeveloper === true;
  if (listing && agents) return 'the listing and the agent';
  if (listing) return 'the listing';
  if (agents) return 'the agent';
  return null;
}

/** How many of some records give each answer: always all three keys. */
export function countByDevelopmentFlag(records) {
  const counts = { [FLAGGED]: 0, [NOT_FLAGGED]: 0, [NO_FLAG]: 0 };
  for (const r of Array.isArray(records) ? records : []) counts[developmentFlagOf(r)] += 1;
  return counts;
}
